import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import { useEffect, useCallback } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Undo, Redo, Minus, Heading1, Heading2, Heading3,
} from "lucide-react";

const BTN = ({ active, title, onClick, children, disabled }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      width: 30, height: 30, border: "none", borderRadius: 6, cursor: "pointer",
      background: active ? "#dbeafe" : "transparent",
      color: active ? "#1976e0" : "#4a5568",
      flexShrink: 0,
      opacity: disabled ? 0.4 : 1,
    }}
    onMouseEnter={(e) => { if (!active && !disabled) e.currentTarget.style.background = "#f4f6f8"; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = active ? "#dbeafe" : "transparent"; }}
  >
    {children}
  </button>
);

const DIVIDER = () => (
  <div style={{ width: 1, height: 20, background: "#dfe3e8", margin: "0 2px", flexShrink: 0 }} />
);

export default function RichTextEditor({ value, onChange, disabled, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { style: "color:#1976e0;text-decoration:underline;" } }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate({ editor }) {
      const html = editor.isEmpty ? "" : editor.getHTML();
      onChange(html);
    },
  });

  // keep editable state in sync
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  // reset when value cleared externally (e.g. after send)
  useEffect(() => {
    if (editor && value === "" && !editor.isEmpty) {
      editor.commands.clearContent(true);
    }
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const COLORS = ["#000000", "#1976e0", "#e53e3e", "#38a169", "#d97706", "#805ad5", "#718096"];

  return (
    <div style={{
      border: "1.5px solid #dfe3e8", borderRadius: 10, overflow: "hidden",
      opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto",
    }}>
      {/* Toolbar */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center",
        padding: "6px 8px", borderBottom: "1px solid #dfe3e8", background: "#f9fafb",
      }}>
        {/* Undo / Redo */}
        <BTN title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo size={15} />
        </BTN>
        <BTN title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo size={15} />
        </BTN>
        <DIVIDER />

        {/* Headings */}
        <BTN title="Heading 1" active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 size={15} />
        </BTN>
        <BTN title="Heading 2" active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 size={15} />
        </BTN>
        <BTN title="Heading 3" active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 size={15} />
        </BTN>
        <DIVIDER />

        {/* Inline formatting */}
        <BTN title="Bold (⌘B)" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={15} />
        </BTN>
        <BTN title="Italic (⌘I)" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={15} />
        </BTN>
        <BTN title="Underline (⌘U)" active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon size={15} />
        </BTN>
        <BTN title="Strikethrough" active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough size={15} />
        </BTN>
        <DIVIDER />

        {/* Lists */}
        <BTN title="Bullet list" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={15} />
        </BTN>
        <BTN title="Numbered list" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={15} />
        </BTN>
        <DIVIDER />

        {/* Alignment */}
        <BTN title="Align left" active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft size={15} />
        </BTN>
        <BTN title="Align center" active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter size={15} />
        </BTN>
        <BTN title="Align right" active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight size={15} />
        </BTN>
        <BTN title="Justify" active={editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
          <AlignJustify size={15} />
        </BTN>
        <DIVIDER />

        {/* Link */}
        <BTN title="Insert / edit link" active={editor.isActive("link")} onClick={setLink}>
          <LinkIcon size={15} />
        </BTN>

        {/* Horizontal rule */}
        <BTN title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus size={15} />
        </BTN>
        <DIVIDER />

        {/* Color swatches */}
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            title={`Text color: ${c}`}
            onClick={() => editor.chain().focus().setColor(c).run()}
            style={{
              width: 18, height: 18, borderRadius: "50%", background: c,
              border: editor.isActive("textStyle", { color: c }) ? "2px solid #1976e0" : "2px solid transparent",
              cursor: "pointer", flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Editor area */}
      <div style={{ position: "relative", minHeight: 160, background: "#fff" }}>
        {editor.isEmpty && (
          <p style={{
            position: "absolute", top: 14, left: 16, margin: 0,
            color: "#a0aec0", fontSize: 14, pointerEvents: "none", userSelect: "none",
          }}>
            {placeholder || "Start typing your message…"}
          </p>
        )}
        <EditorContent
          editor={editor}
          style={{ padding: "12px 16px", minHeight: 160, outline: "none" }}
        />
      </div>

      <style>{`
        .tiptap { outline: none; font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.7; color: #1a1f2e; }
        .tiptap p { margin: 0 0 8px; }
        .tiptap h1 { font-size: 24px; font-weight: 800; margin: 0 0 10px; }
        .tiptap h2 { font-size: 20px; font-weight: 700; margin: 0 0 10px; }
        .tiptap h3 { font-size: 17px; font-weight: 700; margin: 0 0 8px; }
        .tiptap ul, .tiptap ol { padding-left: 22px; margin: 0 0 8px; }
        .tiptap li { margin-bottom: 3px; }
        .tiptap hr { border: none; border-top: 1px solid #dfe3e8; margin: 12px 0; }
        .tiptap a { color: #1976e0; text-decoration: underline; }
      `}</style>
    </div>
  );
}
